import * as React from 'react';
import { cn } from '../../lib/utils';
import { textareaSizeVariants } from '../../lib/fieldSize';
import { Label } from '../Label';
import {
  floatingFieldLabelClassName,
  floatingFieldLabelInsetClassName,
} from '../Label/helpers';
import type { TextareaProps } from './types';

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, id, label, labelProps, required, size, ...rest }, ref) => {
    const generatedId = React.useId();
    const shouldRenderLabel = label != null;
    const textareaId = id ?? (shouldRenderLabel ? generatedId : undefined);
    const { className: labelClassName, ...restLabelProps } = labelProps ?? {};

    const textareaElement = (
      <textarea
        ref={ref}
        id={textareaId}
        required={required}
        data-slot='textarea'
        className={cn(
          // Layout and sizing
          'eye:min-h-96 eye:w-full eye:min-w-0 eye:rounded-md eye:border',
          textareaSizeVariants({ size }),
          // Typography and placeholder
          'eye:text-black eye:placeholder:text-grey-placeholder',
          // Surface
          'eye:border-grey-100 eye:bg-grey-5',
          // Interaction states
          'eye:resize-y eye:outline-offset-3 eye:outline-black eye:transition-[border-color,outline-color,box-shadow]',
          'eye:hover:border-black',
          'eye:disabled:cursor-not-allowed eye:disabled:border-grey-100 eye:disabled:bg-grey-disabled eye:disabled:text-grey-disabled',
          // Focus state
          'eye:focus:border-black eye:focus:outline-2 eye:focus:outline-black',
          // Validation state
          'eye:aria-invalid:border-red-error eye:aria-invalid:hover:border-red-error eye:aria-invalid:focus:border-red-error',
          className,
        )}
        {...rest}
      />
    );

    if (!shouldRenderLabel) {
      return textareaElement;
    }

    return (
      <div
        data-slot='textarea-with-label'
        className='eye:group/textarea-with-label eye:relative eye:block eye:w-full'
      >
        <Label
          htmlFor={textareaId}
          className={cn(
            floatingFieldLabelClassName,
            floatingFieldLabelInsetClassName,
            'eye:top-1',
            // Disabled states
            'eye:group-has-disabled/textarea-with-label:text-grey-disabled',
            // Validation states
            'eye:group-has-[[data-slot=textarea][aria-invalid=true]]/textarea-with-label:text-red-100',
            labelClassName,
          )}
          {...restLabelProps}
        >
          {required ? `${label} *` : label}
        </Label>
        {textareaElement}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';

export { Textarea };
